import axios from "axios";
import { AlertTriangle, Camera, Check, Loader2, MapPin, Phone, User, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CartaoDoCliente } from "@/features/cartao-fidelidade";
import { BuscaBairro } from "@/features/novo-pedido-endereco";
import { acharBairro, cepUnicoDosBairros, consultarCep, nomeDoBairro } from "@/features/novo-pedido-endereco/cep";

import { dataParaGravar, dataParaTela, errosDoCliente, mascararCep, mascararCpf, mascararData, mascararTelefone, soDigitos } from "./regras";

const API = "/api";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const VAZIO = {
  nome: "", telefone: "", email: "", cpf: "", data_nascimento: "", genero: "", foto: "",
  cep: "", endereco: "", numero: "", complemento: "", bairro: "",
};
const DICA_CEP = {
  buscando: "Buscando…",
  achou: "Rua e bairro preenchidos.",
  so_bairro: "Bairro achado pelo CEP; preencha a rua.",
  fora_da_lista: null, // o bairro fica vazio para escolher na lista (sem frase, pedido do dono)
  nao_achou: "CEP não encontrado; preencha à mão.",
};
const GENEROS = [["feminino", "Feminino"], ["masculino", "Masculino"], ["", "Não informar"]];

function Campo({ label, erro, dica, obrigatorio, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-medium">{label}{obrigatorio && <span className="text-red-500"> *</span>}</Label>
      <div className="mt-1">{children}</div>
      {erro ? <p className="text-[11px] text-red-500 mt-1">{erro}</p> : dica ? <p className="text-[11px] text-muted-foreground mt-1">{dica}</p> : null}
    </div>
  );
}

/**
 * Cadastro e edição de cliente (Pessoas e Fornecedores → Clientes), refeito a
 * pedido do dono (25/10/2026): o essencial em cima (nome e WhatsApp, os únicos
 * obrigatórios), aviso de telefone já cadastrado, dados opcionais num bloco
 * separado e o endereço começando pelo CEP, que preenche rua e bairro. Sem o
 * campo Pontuação (o clube de pontos foi removido em 27/10/2026).
 */
export default function ClienteFormDialog({ open, onOpenChange, cliente = null, onSalvo, onAbrirExistente }) {
  const editando = !!cliente?.id;
  const [f, setF] = useState(VAZIO);
  const [inicial, setInicial] = useState(VAZIO);
  const [tentou, setTentou] = useState(false);
  const [tocados, setTocados] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [existente, setExistente] = useState(null);
  const [cepSituacao, setCepSituacao] = useState(null); // buscando | achou | so_bairro | fora_da_lista | nao_achou
  const [bairros, setBairros] = useState([]);
  const ruaRef = useRef(null);
  const numeroRef = useRef(null);
  const complementoRef = useRef(null);
  // O que o último CEP preencheu: trocar o CEP troca rua e bairro que vieram
  // dele, mas nunca o que a pessoa digitou à mão
  const doCepAnterior = useRef({ rua: "", bairro: "" });

  // Bairros de Localização → Bairros: o bairro do cliente sai dessa lista (é ela
  // que dá a taxa de entrega) e o CEP é procurado nela antes do ViaCEP
  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    axios.get(`${API}/bairros`, auth()).then((r) => vivo && setBairros(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    return () => { vivo = false; };
  }, [open]);
  const cepUnico = cepUnicoDosBairros(bairros);

  useEffect(() => {
    if (!open) return;
    // O que veio do banco entra já mascarado: telefone e CPF só com dígitos e
    // aniversário em AAAA-MM-DD (gravado pelo cardápio) virariam erro de formato
    const carregado = cliente ? {
      ...Object.fromEntries(Object.keys(VAZIO).map((k) => [k, cliente[k] || ""])),
      telefone: mascararTelefone(cliente.telefone), cpf: mascararCpf(cliente.cpf), cep: mascararCep(cliente.cep),
      data_nascimento: dataParaTela(cliente.data_nascimento),
    } : VAZIO;
    setF(carregado); setInicial(carregado);
    setTentou(false); setTocados({}); setExistente(null); setCepSituacao(null);
  }, [open, cliente]);

  const muda = (campo, valor) => setF((x) => ({ ...x, [campo]: valor }));
  const toca = (campo) => setTocados((t) => ({ ...t, [campo]: true }));
  const erros = errosDoCliente(f, inicial);
  const erroDe = (campo) => ((tentou || tocados[campo]) ? erros[campo] : null);

  // Telefone completo: confere se já é cliente (o backend devolveria o antigo calado)
  const telDigitos = soDigitos(f.telefone);
  useEffect(() => {
    if (!open || telDigitos.length < 10) { setExistente(null); return undefined; }
    let vivo = true;
    const t = setTimeout(() => {
      axios.get(`${API}/clientes/search/${telDigitos}?leve=1&limite=3`, auth())
        .then((r) => {
          if (!vivo) return;
          const achou = (r.data || []).find((c) => soDigitos(c.telefone).endsWith(telDigitos.slice(-8)) && c.id !== cliente?.id);
          setExistente(achou || null);
        })
        .catch(() => vivo && setExistente(null));
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [open, telDigitos, cliente?.id]);

  // Bairro escolhido da lista: o CEP vazio herda o do bairro
  const escolherBairro = (b) => {
    if (!b) return;
    setF((x) => ({ ...x, bairro: nomeDoBairro(b), cep: x.cep || mascararCep(b.cep) }));
  };

  // Mesma regra do Novo Pedido (useCepEndereco): primeiro o bairro cadastrado
  // com esse CEP; depois o ViaCEP, que dá a rua e o nome do bairro — e esse
  // nome só vale se estiver na lista de bairros da loja
  const aoDigitarCep = async (valor) => {
    const cep = mascararCep(valor);
    muda("cep", cep);
    const d = soDigitos(cep);
    if (d.length !== 8) { setCepSituacao(null); return; }
    const ant = doCepAnterior.current;
    const podeTrocar = (atual, veio) => !atual || atual === veio;
    const preencher = (rua, bairro) => {
      setF((x) => ({
        ...x,
        endereco: rua != null && podeTrocar(x.endereco, ant.rua) ? rua : x.endereco,
        bairro: podeTrocar(x.bairro, ant.bairro) ? bairro : x.bairro,
      }));
      doCepAnterior.current = { rua: rua ?? ant.rua, bairro };
    };
    const doCep = acharBairro(bairros, { cep: d });
    setCepSituacao("buscando");
    const r = await consultarCep(d);
    if (soDigitos(mascararCep(valor)) !== d) return;
    if (!r) {
      preencher(null, doCep ? nomeDoBairro(doCep) : "");
      setCepSituacao(doCep ? "so_bairro" : "nao_achou");
      ruaRef.current?.focus();
      return;
    }
    const daLista = doCep || acharBairro(bairros, { cep: d, nome: r.bairro });
    // Sem lista de bairros na loja, vale o bairro do CEP; com lista, só o que está nela
    preencher(r.rua || "", daLista ? nomeDoBairro(daLista) : bairros.length ? "" : r.bairro);
    setCepSituacao(daLista || !bairros.length ? "achou" : "fora_da_lista");
    (r.rua ? numeroRef : ruaRef).current?.focus();
  };

  const aoEscolherFoto = (e) => {
    const arq = e.target.files?.[0];
    if (!arq) return;
    if (arq.size > 3 * 1024 * 1024) { toast.error("Foto muito grande (máx. 3 MB)."); return; }
    const leitor = new FileReader();
    leitor.onloadend = () => muda("foto", leitor.result);
    leitor.readAsDataURL(arq);
  };

  const salvar = async () => {
    setTentou(true);
    if (Object.keys(erros).length) { toast.error("Confira os campos destacados."); return; }
    if (existente && !editando) { toast.error(`Esse WhatsApp já é de ${existente.nome}. Abra o cadastro dele.`); return; }
    setSalvando(true);
    // Telefone e CPF que não mudaram voltam como estavam gravados (só a máscara
    // da tela mudaria o formato no banco)
    const igual = (k) => cliente && soDigitos(f[k]) === soDigitos(cliente[k]);
    const dados = { ...f, nome: f.nome.trim(), email: f.email.trim(),
      telefone: igual("telefone") ? cliente.telefone : f.telefone,
      cpf: igual("cpf") ? (cliente.cpf || "") : f.cpf,
      data_nascimento: dataParaGravar(f.data_nascimento, cliente?.data_nascimento) };
    try {
      if (editando) await axios.put(`${API}/clientes/${cliente.id}`, dados, auth());
      else await axios.post(`${API}/clientes`, dados, auth());
      toast.success(editando ? "Cliente atualizado!" : "Cliente cadastrado!");
      onOpenChange(false);
      onSalvo?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar o cliente.");
    } finally {
      setSalvando(false);
    }
  };

  const aoTeclar = (e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); salvar(); } };
  const cls = (campo) => (erroDe(campo) ? "border-red-500 focus-visible:ring-red-500/30" : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92dvh] overflow-y-auto" onKeyDown={aoTeclar} data-e2e="cliente-form">
        <DialogHeader>
          <DialogTitle>{editando ? "Editar cliente" : "Novo cliente"}</DialogTitle>
          <DialogDescription className="sr-only">Cadastro do cliente: nome e WhatsApp são obrigatórios.</DialogDescription>
        </DialogHeader>

        {/* Essencial */}
        <div className="flex gap-4 items-start">
          <label className="shrink-0 cursor-pointer group" title="Foto do cliente (opcional)">
            <div className="relative w-20 h-20 rounded-full border-2 border-dashed bg-muted overflow-hidden grid place-items-center group-hover:border-primary/60 transition-colors">
              {f.foto ? <img src={f.foto} alt="" className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-muted-foreground/60" />}
              <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] py-0.5 flex items-center justify-center gap-1"><Camera className="w-3 h-3" />{f.foto ? "Trocar" : "Foto"}</span>
            </div>
            <input type="file" accept="image/*" onChange={aoEscolherFoto} className="hidden" />
          </label>
          <div className="flex-1 grid gap-3 sm:grid-cols-2">
            <Campo label="Nome" obrigatorio erro={erroDe("nome")}>
              <Input value={f.nome} onChange={(e) => muda("nome", e.target.value)} onBlur={() => toca("nome")} autoFocus
                placeholder="Ex.: Juliana Ferreira" className={cls("nome")} data-e2e="cliente-nome" maxLength={80} />
            </Campo>
            <Campo label="WhatsApp" obrigatorio erro={erroDe("telefone")} dica="Com DDD. É por ele que o cliente é achado.">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={f.telefone} onChange={(e) => muda("telefone", mascararTelefone(e.target.value))} onBlur={() => toca("telefone")}
                  inputMode="tel" placeholder="(11) 9.1234-5678" className={`pl-9 ${cls("telefone")}`} data-e2e="cliente-telefone" />
              </div>
            </Campo>
          </div>
        </div>

        {existente && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm" data-e2e="cliente-ja-existe">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <span className="flex-1 min-w-0">Esse WhatsApp já é de <b>{existente.nome}</b>.</span>
            {onAbrirExistente && (
              <Button type="button" size="sm" variant="outline" onClick={() => onAbrirExistente(existente)}>Abrir cadastro</Button>
            )}
          </div>
        )}

        {/* Opcionais */}
        <div className="rounded-xl border p-3 sm:p-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mais sobre o cliente <span className="normal-case font-normal">(opcional)</span></p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo label="E-mail" erro={erroDe("email")}>
              <Input type="email" value={f.email} onChange={(e) => muda("email", e.target.value)} onBlur={() => toca("email")}
                placeholder="Ex.: juliana@email.com" className={cls("email")} data-e2e="cliente-email" />
            </Campo>
            <Campo label="CPF" erro={erroDe("cpf")} dica="Para a nota fiscal e o cashback no Totem.">
              <Input value={f.cpf} onChange={(e) => muda("cpf", mascararCpf(e.target.value))} onBlur={() => toca("cpf")}
                inputMode="numeric" placeholder="000.000.000-00" className={cls("cpf")} data-e2e="cliente-cpf" />
            </Campo>
            <Campo label="Aniversário" erro={erroDe("data_nascimento")} dica="Para a campanha de aniversário.">
              <Input value={f.data_nascimento} onChange={(e) => muda("data_nascimento", mascararData(e.target.value))} onBlur={() => toca("data_nascimento")}
                inputMode="numeric" placeholder="DD/MM/AAAA" className={cls("data_nascimento")} data-e2e="cliente-nascimento" />
            </Campo>
            <Campo label="Gênero">
              <div className="flex rounded-lg border overflow-hidden h-10" role="radiogroup">
                {GENEROS.map(([valor, rotulo], i) => (
                  <button key={rotulo} type="button" role="radio" aria-checked={f.genero === valor} onClick={() => muda("genero", valor)}
                    className={`flex-1 text-xs sm:text-sm font-medium transition-colors ${i ? "border-l" : ""} ${f.genero === valor ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}>
                    {rotulo}
                  </button>
                ))}
              </div>
            </Campo>
          </div>
        </div>

        {/* Endereço */}
        <div className="rounded-xl border p-3 sm:p-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Endereço de entrega <span className="normal-case font-normal">(opcional)</span>
          </p>
          <div className="grid gap-3 grid-cols-6">
            <Campo label="CEP" className="col-span-6 sm:col-span-2" erro={erroDe("cep")} dica={cepSituacao in DICA_CEP ? DICA_CEP[cepSituacao] : (cepUnico ? `CEP único da cidade (${cepUnico}): é só escolher o bairro.` : "Acha rua e bairro.")}>
              <div className="relative">
                <Input value={f.cep} onChange={(e) => aoDigitarCep(e.target.value)} onBlur={() => toca("cep")}
                  inputMode="numeric" placeholder={cepUnico || "00000-000"} className={cls("cep")} data-e2e="cliente-cep" />
                {cepSituacao === "buscando" && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                {(cepSituacao === "achou" || cepSituacao === "so_bairro") && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />}
                {(cepSituacao === "nao_achou" || cepSituacao === "fora_da_lista") && <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />}
              </div>
            </Campo>
            <Campo label="Rua" className="col-span-6 sm:col-span-4">
              <Input ref={ruaRef} value={f.endereco} onChange={(e) => muda("endereco", e.target.value)} placeholder="Ex.: Av. Brasil" data-e2e="cliente-rua" />
            </Campo>
            <Campo label="Número" className="col-span-2">
              <Input ref={numeroRef} value={f.numero} onChange={(e) => muda("numero", e.target.value)} placeholder="Ex.: 450" data-e2e="cliente-numero" />
            </Campo>
            <Campo label="Bairro" className="col-span-4">
              {bairros.length ? (
                <BuscaBairro bairros={bairros} valor={f.bairro} onEscolher={escolherBairro} onEscolhido={() => complementoRef.current?.focus()} />
              ) : (
                <Input value={f.bairro} onChange={(e) => muda("bairro", e.target.value)} placeholder="Ex.: Jardim América" data-e2e="cliente-bairro" />
              )}
            </Campo>
            <Campo label="Complemento" className="col-span-6">
              <Input ref={complementoRef} value={f.complemento} onChange={(e) => muda("complemento", e.target.value)} placeholder="Ex.: Apto 12, bloco B" data-e2e="cliente-complemento" />
            </Campo>
          </div>
        </div>

        {editando && <CartaoDoCliente clienteId={cliente.id} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={salvando} data-e2e="cliente-salvar">
            {salvando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando…</> : editando ? "Salvar alterações" : "Cadastrar cliente"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
